import { useState, useCallback, useRef } from 'react';
import { RegisterCode } from './RegisterCode';
import { Email } from './Email';
import { LoginCode } from './LoginCode';
import { SentryError } from '@ifixit/sentry';
import { Auth } from 'Shared/auth';
import { Recaptcha } from 'Shared/recaptcha';
import { recaptchaSiteKeyFor } from 'Shared/recaptcha-site-key';
import type { FlowStep, AuthInstance, AuthResponse } from './types';
import { useTranslations } from '@ifixit/i18n/hooks';

export function Join({
   privacyPolicyUrl,
   handleGoToLogin,
   onComplete,
}: {
   privacyPolicyUrl: string;
   handleGoToLogin?: () => void;
   onComplete?: () => void;
}) {
   const [flowStep, setFlowStep] = useState<FlowStep>('email');
   const [userEmail, setUserEmail] = useState<string>('');
   const recaptchaRef = useRef<HTMLDivElement>(null);
   const t = useTranslations('Login');

   const handleNavigateToLogin = useCallback(async (email: string) => {
      try {
         const siteKey = recaptchaSiteKeyFor('login');
         let captchaResult: string | undefined;
         if (siteKey) {
            captchaResult = await Recaptcha.executeInvisible(
               recaptchaRef.current,
               siteKey,
               'login'
            );
         }

         const auth = Auth as unknown as AuthInstance;
         await new Promise<void>((resolve, reject) => {
            void auth.sendGetLoginCode(
               { login: email, captchaResult },
               (response: AuthResponse) => {
                  if (response.loggedIn === false && response.reason) {
                     reject(new SentryError(`Login code request failed: ${response.reason}`));
                  } else {
                     resolve();
                  }
               }
            );
         });
         setUserEmail(email);
         setFlowStep('login');
      } catch (error) {
         console.error('Failed to send login code:', error);
      }
   }, []);

   const handleNavigateToRegister = useCallback(async (email: string) => {
      try {
         const siteKey = recaptchaSiteKeyFor('register');
         let captchaResult: string | undefined;
         if (siteKey) {
            captchaResult = await Recaptcha.executeInvisible(
               recaptchaRef.current,
               siteKey,
               'register'
            );
         }

         const auth = Auth as unknown as AuthInstance;
         await new Promise<void>((resolve, reject) => {
            void auth.sendGetRegisterCode(
               { login: email, captchaResult },
               (response: AuthResponse) => {
                  if (response.loggedIn === false && response.reason) {
                     reject(new SentryError(`Register code request failed: ${response.reason}`));
                  } else {
                     resolve();
                  }
               }
            );
         });
         setUserEmail(email);
         setFlowStep('register');
      } catch (error) {
         console.error('Failed to send register code:', error);
      }
   }, []);

   const handleBackToEmail = useCallback(() => {
      setFlowStep('email');
   }, []);

   const loginLinkProps = { className: 'text-brand-500 font-medium', 'data-test-id': 'loginLink' };

   return (
      <div className="max-sm:w-full">
         <div className="text-center text-base mt-6 mb-4 w-full">
            <span>{t('alreadyHaveAnAccount')}</span>
            &nbsp;
            {handleGoToLogin ? (
               <button onClick={handleGoToLogin} {...loginLinkProps}>
                  {t('logIn')}
               </button>
            ) : (
               <a href="/LogIn" {...loginLinkProps}>
                  {t('logIn')}
               </a>
            )}
            <span>.</span>
         </div>
         <div className="w-full lg:w-auto flex flex-col">
            {flowStep === 'email' && (
               <Email
                  onNavigateToLogin={handleNavigateToLogin}
                  onNavigateToRegister={handleNavigateToRegister}
               />
            )}
            {flowStep === 'login' && (
               <LoginCode
                  email={userEmail}
                  onNavigateBack={handleBackToEmail}
                  onLoginComplete={onComplete}
               />
            )}
            {flowStep === 'register' && (
               <RegisterCode
                  email={userEmail}
                  privacyPolicyUrl={privacyPolicyUrl}
                  onJoinComplete={onComplete}
               />
            )}
         </div>
         <div ref={recaptchaRef} className="hidden" />
      </div>
   );
}
